import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Navigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import Icon from '../../components/AppIcon';
import MainNavigation from '../../components/ui/MainNavigation';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';

const ResellerCRMDashboard = () => {
  const { user, userProfile, loading } = useAuth();
  const [activeView, setActiveView] = useState('portfolio');
  const [customers, setCustomers] = useState([]);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [projectTypeFilter, setProjectTypeFilter] = useState('all');
  const [isAddingCustomer, setIsAddingCustomer] = useState(false);
  const [metrics, setMetrics] = useState({
    totalCustomers: 0,
    activeProjects: 0,
    totalRevenue: 0,
    monthlyRevenue: 0
  });
  const [dataLoading, setDataLoading] = useState(true);

  const [newCustomer, setNewCustomer] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    customer_address: '',
    postal_code: '',
    lead_source: 'partner',
    engagement_level: 'medium',
    notes: ''
  });

  useEffect(() => {
    if (user?.id) {
      loadCustomers();
      loadRevenueData();
    }
  }, [user]);

  useEffect(() => {
    filterCustomers();
  }, [customers, searchTerm, projectTypeFilter]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Laden...</p>
        </div>
      </div>
    );
  }

  if (!user || userProfile?.user_type !== 'reseller') {
    return <Navigate to="/provider-login" replace />;
  }

  const loadCustomers = async () => {
    try {
      setDataLoading(true);
      const { data, error } = await supabase
        ?.from('crm_customers')
        ?.select('*')
        ?.eq('provider_id', user?.id)
        ?.order('created_at', { ascending: false });

      if (error) throw error;
      setCustomers(data || []);

      // Calculate customer metrics
      const total = data?.length || 0;
      const active = data?.filter(c => c?.engagement_level === 'high')?.length || 0;

      setMetrics(prev => ({
        ...prev,
        totalCustomers: total,
        activeProjects: active
      }));
    } catch (error) {
      console.error('Error loading customers:', error);
    } finally {
      setDataLoading(false);
    }
  };

  const loadRevenueData = async () => {
    try {
      const { data, error } = await supabase
        ?.from('provider_sales')
        ?.select('*')
        ?.eq('provider_id', user?.id);

      if (error) throw error;

      const total = data?.reduce((sum, sale) => sum + parseFloat(sale?.sale_amount || 0), 0) || 0;
      
      // Calculate current month revenue
      const currentMonth = new Date()?.getMonth();
      const currentYear = new Date()?.getFullYear();
      const monthlyTotal = data?.filter(sale => {
        const saleDate = new Date(sale?.created_at);
        return saleDate?.getMonth() === currentMonth && saleDate?.getFullYear() === currentYear;
      })?.reduce((sum, sale) => sum + parseFloat(sale?.sale_amount || 0), 0) || 0;

      setMetrics(prev => ({
        ...prev,
        totalRevenue: Math.round(total),
        monthlyRevenue: Math.round(monthlyTotal)
      }));
    } catch (error) {
      console.error('Error loading revenue data:', error);
    }
  };

  const filterCustomers = () => {
    let filtered = [...customers];

    if (searchTerm) {
      filtered = filtered?.filter(
        (c) =>
          c?.customer_name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
          c?.customer_email?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
          c?.customer_phone?.includes(searchTerm)
      );
    }

    if (projectTypeFilter !== 'all') {
      filtered = filtered?.filter((c) => c?.lead_source === projectTypeFilter);
    }

    setFilteredCustomers(filtered);
  };

  const handleAddCustomer = async (e) => {
    e?.preventDefault();

    try {
      const { data, error } = await supabase?.from('crm_customers')?.insert([{
        ...newCustomer,
        provider_id: user?.id
      }])?.select();

      if (error) throw error;

      setCustomers([data?.[0], ...customers]);
      setIsAddingCustomer(false);
      setNewCustomer({
        customer_name: '',
        customer_email: '',
        customer_phone: '',
        customer_address: '',
        postal_code: '',
        lead_source: 'partner',
        engagement_level: 'medium',
        notes: ''
      });
    } catch (error) {
      console.error('Error adding customer:', error);
      alert('Fout bij toevoegen klant. Probeer opnieuw.');
    }
  };

  const getEngagementBadge = (level) => {
    const styles = {
      low: 'bg-muted text-muted-foreground border-border',
      medium: 'bg-warning/10 text-warning border-warning/20',
      high: 'bg-success/10 text-success border-success/20',
    };
    const labels = {
      low: 'Inactief',
      medium: 'Actief',
      high: 'Zeer Actief',
    };
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${styles?.[level]}`}>
        {labels?.[level]}
      </span>
    );
  };

  const views = [
    { id: 'portfolio', label: 'Klantportfolio', icon: 'Users' },
    { id: 'network', label: 'Provider Netwerk', icon: 'Network' },
    { id: 'revenue', label: 'Omzet Analytics', icon: 'DollarSign' },
  ];

  const renderPortfolioView = () => (
    <div className="space-y-6">
      {/* Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Totaal Klanten</p>
              <p className="text-3xl font-bold text-foreground mt-2">{metrics?.totalCustomers}</p>
            </div>
            <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
              <Icon name="Users" size={24} className="text-primary" />
            </div>
          </div>
        </div>

        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Actieve Projecten</p>
              <p className="text-3xl font-bold text-foreground mt-2">{metrics?.activeProjects}</p>
            </div>
            <div className="w-12 h-12 bg-success/10 rounded-lg flex items-center justify-center">
              <Icon name="Briefcase" size={24} className="text-success" />
            </div>
          </div>
        </div>

        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Totale Omzet</p>
              <p className="text-3xl font-bold text-foreground mt-2">€{metrics?.totalRevenue?.toLocaleString('nl-NL')}</p>
            </div>
            <div className="w-12 h-12 bg-info/10 rounded-lg flex items-center justify-center">
              <Icon name="Euro" size={24} className="text-info" />
            </div>
          </div>
        </div>

        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Deze Maand</p>
              <p className="text-3xl font-bold text-foreground mt-2">€{metrics?.monthlyRevenue?.toLocaleString('nl-NL')}</p>
            </div>
            <div className="w-12 h-12 bg-warning/10 rounded-lg flex items-center justify-center">
              <Icon name="TrendingUp" size={24} className="text-warning" />
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filter */}
      <div className="bg-card border border-border rounded-lg p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-2">
            <Input
              placeholder="Zoek op naam, email of telefoon..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e?.target?.value)}
            />
          </div>
          <Select
            value={projectTypeFilter}
            onChange={(e) => setProjectTypeFilter(e?.target?.value)}
            options={[
              { value: 'all', label: 'Alle bronnen' },
              { value: 'partner', label: 'Partner' },
              { value: 'direct', label: 'Direct' },
              { value: 'referral', label: 'Verwijzing' },
            ]}
          />
        </div>
      </div>

      {/* Add Customer Button */}
      <div className="flex justify-end">
        <Button onClick={() => setIsAddingCustomer(true)} iconName="Plus">
          Nieuwe Klant Toevoegen
        </Button>
      </div>

      {/* Customer List */}
      <div className="bg-card border border-border rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/50 border-b border-border">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Klant
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Contact
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Locatie
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Bron
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Acties
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {dataLoading ? (
                <tr>
                  <td colSpan="6" className="px-6 py-8 text-center text-muted-foreground">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
                    Laden...
                  </td>
                </tr>
              ) : filteredCustomers?.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-8 text-center text-muted-foreground">
                    Geen klanten gevonden
                  </td>
                </tr>
              ) : (
                filteredCustomers?.map((customer) => (
                  <tr key={customer?.id} className="hover:bg-muted/30 transition-colors">
                    <td className="px-6 py-4">
                      <div className="font-medium text-foreground">{customer?.customer_name}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-muted-foreground">{customer?.customer_email}</div>
                      <div className="text-sm text-muted-foreground">{customer?.customer_phone}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-muted-foreground">{customer?.postal_code}</div>
                    </td>
                    <td className="px-6 py-4">
                      {getEngagementBadge(customer?.engagement_level)}
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm text-muted-foreground capitalize">{customer?.lead_source}</span>
                    </td>
                    <td className="px-6 py-4">
                      <Button variant="ghost" size="sm" iconName="Eye">
                        Details
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  const renderNetworkView = () => (
    <div className="space-y-6">
      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">Provider Netwerk Overzicht</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="border border-border rounded-lg p-4">
            <h4 className="font-medium text-foreground mb-2">Actieve Partners</h4>
            <p className="text-3xl font-bold text-primary">0</p>
            <p className="text-sm text-muted-foreground mt-1">Geregistreerde providers</p>
          </div>
          <div className="border border-border rounded-lg p-4">
            <h4 className="font-medium text-foreground mb-2">Gemiddelde Kwaliteit</h4>
            <p className="text-3xl font-bold text-success">-</p>
            <p className="text-sm text-muted-foreground mt-1">Partner beoordelingen</p>
          </div>
          <div className="border border-border rounded-lg p-4">
            <h4 className="font-medium text-foreground mb-2">Capaciteit</h4>
            <p className="text-3xl font-bold text-warning">-</p>
            <p className="text-sm text-muted-foreground mt-1">Beschikbare projecten</p>
          </div>
        </div>
      </div>
    </div>
  );

  const renderRevenueView = () => (
    <div className="space-y-6">
      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">Omzet Analytics</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h4 className="font-medium text-muted-foreground mb-2">Commissie Breakdown</h4>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm">Totale Commissie</span>
                <span className="font-semibold">€{Math.round(metrics?.totalRevenue * 0.15)?.toLocaleString('nl-NL')}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm">Deze Maand</span>
                <span className="font-semibold text-success">€{Math.round(metrics?.monthlyRevenue * 0.15)?.toLocaleString('nl-NL')}</span>
              </div>
            </div>
          </div>
          <div>
            <h4 className="font-medium text-muted-foreground mb-2">Winstgevendheid</h4>
            <div className="text-3xl font-bold text-foreground">€{metrics?.totalRevenue?.toLocaleString('nl-NL')}</div>
            <p className="text-sm text-muted-foreground mt-1">Totale projectwaarde</p>
          </div>
        </div>
      </div>
    </div>
  );

  const renderContent = () => {
    switch (activeView) {
      case 'portfolio':
        return renderPortfolioView();
      case 'network':
        return renderNetworkView();
      case 'revenue':
        return renderRevenueView();
      default:
        return renderPortfolioView();
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <MainNavigation />

      <main className="pt-16 lg:pt-0 lg:ml-[72px] transition-all duration-300">
        {/* Header */}
        <div className="bg-card border-b border-border">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-heading font-bold text-foreground flex items-center space-x-3">
                  <div className="w-12 h-12 bg-primary rounded-lg flex items-center justify-center">
                    <Icon name="Building" size={24} color="white" />
                  </div>
                  <span>Reseller CRM Dashboard</span>
                </h1>
                <p className="text-muted-foreground mt-2">
                  Beheer uw klantportfolio, provider netwerk en omzet
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* View Navigation */}
        <div className="bg-card border-b border-border">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex space-x-1 overflow-x-auto">
              {views?.map((view) => (
                <button
                  key={view?.id}
                  onClick={() => setActiveView(view?.id)}
                  className={`flex items-center space-x-2 px-6 py-4 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                    activeView === view?.id
                      ? 'border-primary text-primary bg-primary/5' :'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
                  }`}
                >
                  <Icon name={view?.icon} size={18} />
                  <span>{view?.label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {renderContent()}
        </div>
      </main>

      {/* Add Customer Modal */}
      {isAddingCustomer && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card border border-border rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-heading font-bold text-foreground">Nieuwe Klant Toevoegen</h2>
                <button onClick={() => setIsAddingCustomer(false)} className="text-muted-foreground hover:text-foreground">
                  <Icon name="X" size={24} />
                </button>
              </div>

              <form onSubmit={handleAddCustomer} className="space-y-4">
                <Input
                  label="Naam"
                  value={newCustomer?.customer_name}
                  onChange={(e) => setNewCustomer({ ...newCustomer, customer_name: e?.target?.value })}
                  required
                />
                <Input
                  label="Email"
                  type="email"
                  value={newCustomer?.customer_email}
                  onChange={(e) => setNewCustomer({ ...newCustomer, customer_email: e?.target?.value })}
                  required
                />
                <Input
                  label="Telefoon"
                  value={newCustomer?.customer_phone}
                  onChange={(e) => setNewCustomer({ ...newCustomer, customer_phone: e?.target?.value })}
                  required
                />
                <Input
                  label="Adres"
                  value={newCustomer?.customer_address}
                  onChange={(e) => setNewCustomer({ ...newCustomer, customer_address: e?.target?.value })}
                />
                <Input
                  label="Postcode"
                  value={newCustomer?.postal_code}
                  onChange={(e) => setNewCustomer({ ...newCustomer, postal_code: e?.target?.value })}
                />
                <Select
                  label="Acquisitie Bron"
                  value={newCustomer?.lead_source}
                  onChange={(e) => setNewCustomer({ ...newCustomer, lead_source: e?.target?.value })}
                  options={[
                    { value: 'partner', label: 'Partner' },
                    { value: 'direct', label: 'Direct' },
                    { value: 'referral', label: 'Verwijzing' },
                    { value: 'website', label: 'Website' },
                  ]}
                />
                <Select
                  label="Project Status"
                  value={newCustomer?.engagement_level}
                  onChange={(e) => setNewCustomer({ ...newCustomer, engagement_level: e?.target?.value })}
                  options={[
                    { value: 'low', label: 'Inactief' },
                    { value: 'medium', label: 'Actief' },
                    { value: 'high', label: 'Zeer Actief' },
                  ]}
                />
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Notities</label>
                  <textarea
                    value={newCustomer?.notes}
                    onChange={(e) => setNewCustomer({ ...newCustomer, notes: e?.target?.value })}
                    rows="3"
                    className="w-full px-3 py-2 border border-input rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setIsAddingCustomer(false)}>
                    Annuleren
                  </Button>
                  <Button type="submit">
                    Klant Toevoegen
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ResellerCRMDashboard;